import { alchemyWalletTransport, createSmartWalletClient } from '@alchemy/wallet-apis'
import type { Chain, LocalAccount } from 'viem'
import { arbitrum, arbitrumSepolia, base, bsc, polygon } from 'viem/chains'
import type { SponsoredCall, SponsoredChain } from './types'

const SPONSORED_CHAINS: Record<SponsoredChain, Chain> = {
	polygon,
	bsc,
	base,
	arbitrum,
	arbitrumSepolia,
}

export function getSponsoredViemChain(chain: SponsoredChain): Chain {
	return SPONSORED_CHAINS[chain]
}

export function toWalletApiCalls(calls: SponsoredCall | SponsoredCall[]) {
	const list = Array.isArray(calls) ? calls : [calls]
	return list.map(call => ({ to: call.target, data: call.data, value: call.value }))
}

/**
 * Smart wallet client on Alchemy infra for an arbitrary chain. Defaults to
 * EIP-7702 at the signer's own address — same semantics as the previous
 * createModularAccountV2Client({ mode: '7702' }) call sites.
 */
export function createSmartWalletClientForChain({
	chain,
	account,
}: {
	chain: Chain
	account: LocalAccount
}) {
	const policyId = process.env.NEXT_PUBLIC_ALCHEMY_POLICY_ID
	return createSmartWalletClient({
		signer: account,
		transport: alchemyWalletTransport({
			apiKey: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY ?? '',
		}),
		chain,
		paymaster: policyId ? { policyId } : undefined,
	})
}

export function createSponsoredClient({
	chain,
	account,
}: {
	chain: SponsoredChain
	account: LocalAccount
}) {
	return createSmartWalletClientForChain({ chain: SPONSORED_CHAINS[chain], account })
}

type SponsoredClient = ReturnType<typeof createSponsoredClient>

/**
 * Send gas-sponsored calls on an already-created smart wallet client and wait
 * for inclusion. Returns the transaction hash — same contract as the old
 * sendUserOperation + waitForUserOperationTransaction pair. Use this for
 * callers that create the client once (cached in a ref) and send multiple
 * batches with it.
 */
export async function sendSponsoredCallsWithClient(
	client: SponsoredClient,
	calls: SponsoredCall | SponsoredCall[],
	{ silent = false }: { silent?: boolean } = {}
): Promise<`0x${string}`> {
	try {
		const { id } = await client.sendCalls({ calls: toWalletApiCalls(calls) })
		const status = await client.waitForCallsStatus({ id })

		if (status.status !== 'success') {
			throw new Error(`Sponsored call failed with status: ${status.status}`)
		}

		const txHash = status.receipts?.[0]?.transactionHash
		if (!txHash) {
			throw new Error('Sponsored call succeeded but returned no transaction hash')
		}
		return txHash
	} catch (err) {
		if (!silent) {
			// Surface the raw bundler/paymaster error so debug logs aren't just
			// "400 Bad Request" with no payload. Common variants we've hit:
			//  - "policy denied" → contract not on allowlist
			//  - "AA21 didn't pay prefund" → paymaster rejected
			//  - "AA40 over verificationGasLimit" → gas estimation way off
			const e = err as { message?: string; details?: string; cause?: { message?: string } }
			console.error('[wallet-core] sendCalls failed', {
				message: e?.message,
				details: e?.details,
				cause: e?.cause?.message,
			})
		}
		throw err
	}
}

/**
 * Send gas-sponsored calls via EIP-7702 and wait for inclusion.
 * Returns the transaction hash — same contract as the old
 * waitForUserOperationTransaction flow.
 */
export async function sendSponsoredCalls({
	chain,
	account,
	calls,
	silent = false,
}: {
	chain: SponsoredChain
	account: LocalAccount
	calls: SponsoredCall | SponsoredCall[]
	/**
	 * Suppress the console.error on failure. Callers that wrap this in a
	 * retry loop (e.g. useHyperliquidDeposit which retries Alchemy
	 * paymaster races) set this to true on intermediate attempts so the
	 * DevTools console doesn't fill up with errors that get silently
	 * recovered. Set to `false` (default) on the LAST attempt — that error
	 * is real and the user/operator should see it.
	 */
	silent?: boolean
}): Promise<`0x${string}`> {
	const client = createSponsoredClient({ chain, account })
	return sendSponsoredCallsWithClient(client, calls, { silent })
}
